import {pick} from '@benchling/js-utils';
import {css, StyleSheet, StyleDeclarationValue} from 'aphrodite';
import React, {useState} from 'react';
import {EdgeLabelRenderer, EdgeProps, Position, useReactFlow} from 'reactflow';

import {assert} from 'Asserts';
import {FlowchartActionFunctionButtonToolbar} from 'processes/graph-builder/flowchart-viz/FlowchartActionFunctionButtonToolbar';
import {
  PROCESS_FLOWCHART_EDGE_DEFAULT_COLOR,
  PROCESS_FLOWCHART_EDGE_ERROR_COLOR,
  ProcessFlowchartEdgeType,
  ProcessFlowchartInteractiveEdgeWithActionFunctions,
} from 'processes/graph-builder-processes/flowchart-viz/ProcessFlowchartVizConstants';
import {getEdgeRenderingData} from 'processes/graph-builder-processes/flowchart-viz/util/getEdgeRenderingData';
import useFlowchartDataCache from 'processes/graph-builder-processes/flowchart-viz/util/useFlowchartDataCache';
import getTestProps from 'test/lib/cypress/getTestProps';
import Float from 'ui-library/floating-ui/Float';
import {FloatingInteraction, FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';

import EdgeLabel from './EdgeLabel';
import ProcessFlowchartContext from './ProcessFlowchartContext';

const ACTION_FUNCTION_Y_OFFSET_OVERRIDE = 20;

export interface ProcessFlowchartInteractiveEdgeRendererProps extends EdgeProps {
  data?: ProcessFlowchartInteractiveEdgeWithActionFunctions;
  nodeStyles?: Array<StyleDeclarationValue>;
  sourceX: number;
  sourceY: number;
  targetX: number;
  targetY: number;
  sourcePosition: Position;
  targetPosition: Position;
  markerEnd?: string;
}

interface InteractiveEdgePathProps {
  clientId: string;
  edgeClassName: string;
  /** edgePath and markerEnd are arguments passed through from ReactFlow */
  edgePath: string;
  markerEnd?: string;
  setIsEdgeHovered?: React.Dispatch<React.SetStateAction<boolean>>;
}

export function shouldShowEdgeActionFunctions(
  edge: ProcessFlowchartInteractiveEdgeWithActionFunctions
): boolean {
  return !edge.locked && edge.actionFunctions != null && edge.actionFunctions.length !== 0;
}

function InteractiveEdgePath({
  clientId,
  edgeClassName,
  edgePath,
  markerEnd,
  setIsEdgeHovered,
}: InteractiveEdgePathProps): JSX.Element {
  return (
    <path
      id={clientId}
      className={edgeClassName}
      d={edgePath}
      markerEnd={markerEnd}
      onMouseEnter={() => {
        if (setIsEdgeHovered) {
          setIsEdgeHovered(true);
        }
      }}
      onMouseLeave={() => {
        if (setIsEdgeHovered) {
          setIsEdgeHovered(false);
        }
      }}
    />
  );
}

// TODO(BNCH-43622): Show errors on hover.
export default function ProcessFlowchartInteractiveEdgeRenderer({
  data,
  nodeStyles,
  markerEnd,
  label,
  ...props
}: ProcessFlowchartInteractiveEdgeRendererProps): JSX.Element {
  // Our usage is stricter than the underlying ReactFlow prop implementation
  assert(data != null, 'Unable to render a flowchart edge without data');
  const {hideTooltips, flowchartClientId} = ProcessFlowchartContext.useContext();
  const {getNodes, getNode, getEdges, getZoom} = useReactFlow();
  const {getDataForEdge, setDataForEdge} = useFlowchartDataCache();
  const [zoomScale, setZoomScale] = useState(getZoom());
  const targetNode = getNode(data.toNodeClientId);
  const sourceNode = getNode(data.fromNodeClientId);
  const nodes = getNodes();
  const edges = getEdges();
  const edgeParams = pick(
    props,
    'sourceX',
    'sourceY',
    'targetX',
    'targetY',
    'sourcePosition',
    'targetPosition'
  );
  const baseEdgeData = pick(data, 'clientId', 'fromNodeClientId', 'toNodeClientId');

  const cachedEdgeInfo = getDataForEdge(baseEdgeData.clientId);

  const [isEdgeHovered, setIsEdgeHovered] = useState(false);

  const computedEdgeInfo =
    cachedEdgeInfo != null
      ? cachedEdgeInfo
      : getEdgeRenderingData(
          baseEdgeData,
          edgeParams,
          nodes,
          edges,
          true,
          flowchartClientId,
          sourceNode,
          targetNode,
          data.layout
        );

  if (!cachedEdgeInfo) {
    // computing edge data can be expensive, especially for smartEdges, so we cache it
    setDataForEdge(baseEdgeData.clientId, computedEdgeInfo);
  }

  const {edgePath, labelX, labelY, hoverAreaWidth, hoverAreaHeight, hoverAreaX, hoverAreaY} =
    computedEdgeInfo;
  const edgeClassName = css(
    (data.errors?.length ?? 0) > 0 ? styles.edgeWithErrors : styles.edgeNoErrors,
    ...(nodeStyles ?? [])
  );

  return (
    <>
      <svg
        className={css(styles.edgeSvg)}
        onMouseOver={() => {
          setZoomScale(getZoom());
        }}
      >
        <InteractiveEdgePath
          clientId={data.clientId}
          edgePath={edgePath}
          edgeClassName={edgeClassName}
          markerEnd={markerEnd}
        />
        <foreignObject
          x={hoverAreaX}
          y={hoverAreaY}
          width={hoverAreaWidth}
          height={hoverAreaHeight}
          className={css(styles.hoverArea)}
          onMouseEnter={() => {
            setIsEdgeHovered(true);
          }}
          onMouseLeave={() => {
            setIsEdgeHovered(false);
          }}
        >
          <Float
            interaction={FloatingInteraction.CONTROLLED}
            isOpen={isEdgeHovered}
            placement={FloatingPlacement.CENTER}
            placementOptions={
              label != null ? {offsetOverride: ACTION_FUNCTION_Y_OFFSET_OVERRIDE * getZoom()} : {}
            }
            floatingElementOptions={{
              floatingContentStyles: {transform: `scale(${zoomScale})`},
            }}
            renderContent={() =>
              !hideTooltips &&
              shouldShowEdgeActionFunctions(data) && (
                <FlowchartActionFunctionButtonToolbar actionFunctions={data.actionFunctions!} item={data} />
              )
            }
          >
            <>
              <div
                {...getTestProps({component: 'GraphBuilderInteractiveEdge', element: 'hoverArea'})}
                style={{
                  width: hoverAreaWidth,
                  height: hoverAreaHeight,
                }}
              />
              {label != null && (
                <EdgeLabelRenderer>
                  <EdgeLabel
                    label={label}
                    labelX={labelX}
                    labelY={labelY}
                    labelShowBg={props.labelShowBg}
                    labelStyle={props.labelStyle}
                    labelBgPadding={props.labelBgPadding}
                    labelBgBorderRadius={props.labelBgBorderRadius}
                    labelBgStyle={props.labelBgStyle}
                  />
                </EdgeLabelRenderer>
              )}
            </>
          </Float>
        </foreignObject>
        {/* This transparent edge lies on top of the 'real' edge but has a wider transparent stroke so you can hover over it */}
        <InteractiveEdgePath
          clientId={`${data.clientId}-transparentmouseover`}
          edgePath={edgePath}
          edgeClassName={css(styles.transparentMouseoverEdge)}
          setIsEdgeHovered={setIsEdgeHovered}
        />
      </svg>
    </>
  );
}

const styles = StyleSheet.create({
  edgeSvg: {
    overflow: 'visible',
  },
  hoverArea: {
    overflow: 'visible',
  },
  edgeNoErrors: {stroke: PROCESS_FLOWCHART_EDGE_DEFAULT_COLOR, fill: 'none'},
  edgeWithErrors: {
    stroke: PROCESS_FLOWCHART_EDGE_ERROR_COLOR,
    fill: 'none',
  },
  transparentMouseoverEdge: {
    stroke: 'transparent',
    strokeWidth: '20px',
    fill: 'none',
  },
});

export const PROCESS_FLOWCHART_EDGE_TYPE_TO_INTERACTIVE_EDGE_RENDERER = {
  [ProcessFlowchartEdgeType.INTERACTIVE_BUILDER]: ProcessFlowchartInteractiveEdgeRenderer,
};
